import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { fetchDocumentBlob, downloadDocument } from 'app/api/contractDocumentApi';

const isPreviewableMimeType = (mimeType: string) => {
  if (!mimeType) return false;
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/') ||
    mimeType.startsWith('text/')
  );
};

const DocumentPreviewDialog = ({ visible, onHide, document: contractDocument }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>('');

  useEffect(() => {
    if (!visible || !contractDocument) {
      return;
    }

    let revokedUrl: string | null = null;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setPreviewUrl(null);
    setMimeType('');

    fetchDocumentBlob(contractDocument.id)
      .then(({ url, mimeType: mt }) => {
        if (cancelled) {
          window.URL.revokeObjectURL(url);
          return;
        }
        revokedUrl = url;
        setPreviewUrl(url);
        setMimeType(mt);
      })
      .catch(e => {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Nepodarilo sa načítať dokument.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (revokedUrl) {
        window.URL.revokeObjectURL(revokedUrl);
      }
    };
  }, [visible, contractDocument?.id]);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex justify-content-center" style={{ padding: '2rem' }}>
          <ProgressSpinner />
        </div>
      );
    }

    if (error) {
      return <div className="p-error" style={{ padding: '1rem' }}>{error}</div>;
    }

    if (!previewUrl) {
      return null;
    }

    if (isPreviewableMimeType(mimeType)) {
      if (mimeType.startsWith('image/')) {
        return (
          <div style={{ textAlign: 'center', overflow: 'auto', maxHeight: '75vh' }}>
            <img src={previewUrl} alt={contractDocument?.originalFilename} style={{ maxWidth: '100%', maxHeight: '75vh' }} />
          </div>
        );
      }
      return (
        <iframe
          src={previewUrl}
          title={contractDocument?.originalFilename}
          style={{ width: '100%', height: '75vh', border: 'none' }}
        />
      );
    }

    return (
      <div style={{ padding: '1rem' }}>
        Tento typ súboru ({mimeType || 'neznámy'}) nie je možné zobraziť v prehliadači. Stiahnite ho prosím.
      </div>
    );
  };

  const footer = (
    <Button
      style={{ backgroundColor: '#e31818' }}
      label="Stiahnuť"
      icon="pi pi-download"
      onClick={() => {
        if (contractDocument) void downloadDocument(contractDocument);
      }}
    />
  );

  return (
    <Dialog
      header={contractDocument?.originalFilename || 'Náhľad dokumentu'}
      visible={visible}
      style={{ width: '90vw' }}
      onHide={onHide}
      footer={footer}
      maximizable
    >
      {renderBody()}
    </Dialog>
  );
};

export default DocumentPreviewDialog;
